import React from 'react';
import { Button, Typography, Dropdown, Row, Col, Tooltip } from '../../../components';
import { MoreOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';

import {
    EditIcon,
    MagnifyingGlassMigrateIcon,
    MagnifyingGlassSourceIcon,
    WrenchIcon,
    TrashIcon,
} from '../../../components/icons';
import {
    getMigrationReadiness,
    getMigrationMethod,
    formatTags,
    formatSourceDate,
    getOSDescription,
    getPowerState,
    cloudSizing,
} from '../utils.jsx';

const { Text } = Typography;

export const isInSituEligible = (item) => {
    if (!item || item.collection_type === 'vm' || item.collection_type === 'data_store') {
        return false;
    }

    if (item.os_type == null) {
        return true;
    }

    const os = item?.attributes?.os ? item.attributes.os : null;

    try {
        const stateObj = os?.source_migration_state ? JSON.parse(os.source_migration_state) : null;
        const upgrade_options = stateObj?.upgrade_options;

        if (!upgrade_options || !upgrade_options?.length) {
            return false;
        }

        const upgrade_state = stateObj?.upgrade_state;
        if (upgrade_state && ['running', 'success', 'error', 'cancelled'].includes(upgrade_state?.state)) {
            return false;
        }

        if (item.os_type === 'windows') {
            const platform = item?.attributes?.hardware?.platform;
            if (platform && platform !== 'vmware') {
                return false;
            }
            if (os && ['2003', 'XP', 'Vista', '7', '8', '8.1', '10.0'].includes(os.version)) {
                return false;
            }
            if (os && os.version === '2008' && os.kernel && os.kernel?.startsWith('6.0')) {
                return false;
            }
        } else if (item.os_type === 'linux') {
            if (os?.vendor !== 'Red Hat Enterprise Linux Server') {
                return false;
            }
            const v = Number(os?.version);
            if (!(v >= 7 && v < 9)) {
                return false;
            }
        }

        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Creates the table columns configuration for the Sources table
 *
 * This function generates the column definitions for Antd Table including:
 * - Hierarchical group headers that span multiple columns
 * - Dynamic column visibility based on data content
 * - Integrated action menus for each row
 * - Proper data formatting and display
 *
 * @param {Object} handlers - Action handlers for table interactions
 * @param {Function} handlers.handleStartMigration - Handler for starting migration
 * @param {Function} handlers.handleInspectSource - Handler for source inspection
 * @param {Function} handlers.handleEditSource - Handler for editing source
 * @param {Function} handlers.handleConfigureUpgrade - Handler for upgrade configuration
 * @param {Function} handlers.handleDeleteSingleSource - Handler for deleting single source
 * @param {string} handlers.userOrg - User organization ID
 * @param {number} handlers.pollingCount - Number of sources being polled
 * @param {Array} handlers.expandedRowKeys - Currently expanded group keys for expand/collapse icons
 * @param {Function} handlers.setExpandedRowKeys - Function to update expanded row keys
 * @returns {Array} Column configuration array for Antd Table
 */
export const createTableColumns = ({
    handleStartMigration,
    handleInspectSource,
    handleEditSource,
    handleConfigureUpgrade,
    handleDeleteSingleSource,
    userOrg,
    expandedRowKeys,
    setExpandedRowKeys,
}) => {
    // Action menu for each row
    const getActionMenu = (record) => {
        const items = [
            {
                key: 'edit',
                icon: <EditIcon grey />,
                label: 'Edit',
                onClick: () => handleEditSource(record.id),
                disabled: record.collection_type === 'vm',
            },
            {
                key: 'migrate',
                icon: <MagnifyingGlassMigrateIcon grey />,
                label: 'Inspect & Migrate',
                onClick: () => handleStartMigration(record.id),
            },
            {
                key: 'inspect',
                icon: <MagnifyingGlassSourceIcon grey />,
                label: 'Inspect Source',
                onClick: () => handleInspectSource(record.id),
            },
            {
                key: 'configure',
                icon: <WrenchIcon grey />,
                label: 'Configure In-Situ Upgrade',
                onClick: () => handleConfigureUpgrade(record.id),
                disabled: !isInSituEligible(record),
            },
            {
                key: 'delete',
                icon: <TrashIcon />,
                label: 'Delete Source',
                danger: true,
                onClick: () => handleDeleteSingleSource(record.id),
            },
        ];

        return { items };
    };

    return [
        {
            title: 'Migration Method',
            dataIndex: 'transfer_method',
            key: 'migration_method',
            className: 'group',
            width: 150,
            render: (_, record) => {
                if (record.isGroup) {
                    return (
                        <Row gutter={[32, 8]} align="middle">
                            <Col span={6}>
                                <strong>Move Group:</strong> {record.moveGroupName}
                            </Col>
                            <Col span={8}>
                                <strong>Assigned Resource:</strong>{' '}
                                {record.moveGroup?.assigned_resource_email || 'Not set'}
                            </Col>
                            <Col span={8}>
                                <strong>Target Migration Date:</strong>{' '}
                                {record.moveGroup?.target_migration_date
                                    ? formatSourceDate(record.moveGroup.target_migration_date)
                                    : 'Not set'}
                            </Col>
                        </Row>
                    );
                }
                return getMigrationMethod(record);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 9 }; // Span across all columns except Actions
                }
                return {};
            },
        },
        {
            title: 'IP/VM Name',
            dataIndex: 'host',
            key: 'host',
            width: 140,
            ellipsis: {
                showTitle: false,
            },
            render: (text, record) => {
                if (record.isGroup) return null;
                return (
                    <Text className={'source-ip'} ellipsis={{ tooltip: true }}>
                        {text}
                    </Text>
                );
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 }; // Hide this cell for group rows
                }
                return {};
            },
        },
        {
            title: 'Hostname',
            dataIndex: 'hostname',
            key: 'hostname',
            width: 130,
            ellipsis: {
                showTitle: false,
            },
            render: (text, record) => {
                if (record.isGroup) return null;
                return text ? (
                    <Text className={'hostname'} ellipsis={{ tooltip: true }}>
                        {text}
                    </Text>
                ) : (
                    <Text type={'secondary'} className={'font-size-12'}>{`<Waiting for inspection>`}</Text>
                );
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'OS',
            dataIndex: 'os_type',
            key: 'os',
            width: 90,
            align: 'center',
            render: (_, record) => {
                if (record.isGroup) return null;
                return getOSDescription(record);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            className: 'tags-column',
            key: 'tags',
            width: 220,
            render: (tags, record) => {
                if (record.isGroup) return null;
                return (
                    <Tooltip title="Click to Add/Edit tags">
                        <span>{formatTags(tags)}</span>
                    </Tooltip>
                );
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return { className: 'cursor-pointer' };
            },
        },
        {
            title: 'Cloud Sizing',
            dataIndex: 'cloud_sizing',
            key: 'cloud_sizing',
            align: 'center',
            render: (text, record) => {
                if (record.isGroup) return null;
                return cloudSizing(record);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return { style: { minWidth: 150 } };
            },
        },
        {
            title: 'Power State',
            dataIndex: 'power_state',
            key: 'power_state',
            width: 140,
            render: (_, record) => {
                if (record.isGroup) return null;
                return getPowerState(record);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Migration Readiness',
            dataIndex: 'preflight_state',
            key: 'migration_readiness',
            // width: 150,
            align: 'center',
            render: (_, record) => {
                if (record.isGroup) return null;
                return getMigrationReadiness(record);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 130,
            render: (text, record) => {
                if (record.isGroup) return null;
                return formatSourceDate(text);
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            render: (_, record) => {
                const handleMenuClick = (e) => {
                    e.domEvent.stopPropagation(); // Prevent row click

                    // Handle menu items
                    const menuItems = getActionMenu(record).items;
                    const clickedItem = menuItems.find((item) => item.key === e.key);
                    if (clickedItem && clickedItem.onClick && !clickedItem.disabled) {
                        clickedItem.onClick();
                    }
                };

                if (record.isGroup) {
                    return (
                        <Button
                            type="text"
                            size="small"
                            className={'collapse-icon'}
                            icon={expandedRowKeys.includes(record.key) ? <DownOutlined /> : <RightOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                const isCurrentlyExpanded = expandedRowKeys.includes(record.key);
                                if (isCurrentlyExpanded) {
                                    setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.key));
                                } else {
                                    setExpandedRowKeys([...expandedRowKeys, record.key]);
                                }
                            }}
                        />
                    );
                }

                return (
                    <Dropdown
                        menu={{
                            ...getActionMenu(record),
                            onClick: handleMenuClick,
                        }}
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Dropdown>
                );
            },
            onCell: (record) => {
                if (record.isGroup) {
                    return {
                        className: 'group-row-cell',
                    };
                }
                return {
                    className: 'actions-cell-clickable',
                    onClick: (e) => {
                        e.stopPropagation();
                    },
                };
            },
        },
    ];
};
